import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  EligibilityCheck,
  EligibilityCheckResult,
  VerifyEligibilityInput,
} from '../../types/eligibility';
import { PayerDirectoryService, PayerOption } from '../../services/PayerDirectoryService';
import EligibilityResultCard from './EligibilityResultCard';
import PayerPicker from './PayerPicker';
import VerifyCoverageButton from './VerifyCoverageButton';

/**
 * Everything needed to verify one patient's coverage, in one drop-in panel.
 *
 * Composed rather than wired piecemeal so each integration point (patient
 * billing tab, intake, schedule) is a one-line change instead of four
 * components' worth of state duplicated across large existing files.
 *
 * Deliberately additive: it does not touch the typed-in insurance fields that
 * already exist elsewhere. Those record what a patient told us; this records
 * what the payer says. Reconciling the two is a product decision, not a
 * refactor to make in passing.
 */

/** Only what verification needs — avoids coupling to the full Patient type. */
export interface EligibilityPatient {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  insurance?: {
    provider?: string;
    policyNumber?: string;
    groupNumber?: string;
  };
}

interface PatientEligibilityPanelProps {
  client: EligibilityPatient;
  /** Defaults to the signed-in user. */
  providerId?: string;
  title?: string;
}

const COLORS = {
  panel: '#28527A',
  input: '#111827',
  border: '#374151',
  text: '#f9fafb',
  label: '#d1d5db',
  muted: '#9ca3af',
};

function timestampToDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date(value as string);
}

const PatientEligibilityPanel: React.FC<PatientEligibilityPanelProps> = ({
  client,
  providerId,
  title = 'Insurance Verification',
}) => {
  const { user } = useAuth();
  const effectiveProviderId = providerId || user?.uid || '';

  const [payer, setPayer] = useState<PayerOption | null>(null);
  const [memberId, setMemberId] = useState(client.insurance?.policyNumber || '');
  const [providerNpi, setProviderNpi] = useState('');
  const [check, setCheck] = useState<EligibilityCheck | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [reverifying, setReverifying] = useState(false);

  // Pull the provider's NPI. Without it the payer rejects the request, so it is
  // better to fetch it than to make staff type it on every check.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!effectiveProviderId) return;
      try {
        const snap = await getDoc(doc(db, 'providers', effectiveProviderId));
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as { npiNumber?: string; npi?: string };
        setProviderNpi(data.npiNumber || data.npi || '');
      } catch (error) {
        console.warn('[Eligibility] Could not load provider NPI:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveProviderId]);

  // Prefill the payer from whatever free text is already on the record. It is a
  // guess, so it is shown as a normal selection the user can correct.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const existing = client.insurance?.provider;
      if (!existing) return;
      const matches = await PayerDirectoryService.search(existing, 1);
      if (!cancelled && matches.length) setPayer(matches[0]);
    })();

    return () => {
      cancelled = true;
    };
  }, [client.insurance?.provider]);

  // Most recent stored check, so re-opening the patient does not re-spend money.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoadingHistory(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, 'eligibility_checks'),
            where('patientId', '==', client.id),
            orderBy('checkedAt', 'desc'),
            limit(1)
          )
        );

        if (cancelled || snap.empty) return;

        const data = snap.docs[0].data();
        setCheck({
          ...(data as EligibilityCheck),
          id: snap.docs[0].id,
          checkedAt: timestampToDate(data.checkedAt),
          expiresAt: timestampToDate(data.expiresAt),
        });
        setFromCache(true);
      } catch (error) {
        // No stored history is normal in mock mode — the client cannot write.
        console.warn('[Eligibility] Could not load check history:', error);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client.id]);

  const input: VerifyEligibilityInput | null = useMemo(() => {
    if (!payer) return null;
    return {
      patientId: client.id,
      payerId: payer.id,
      payerName: payer.name,
      stediPayerId: payer.stediPayerId,
      providerId: effectiveProviderId,
      providerNpi,
      practiceId: effectiveProviderId,
      memberId: memberId.trim(),
      firstName: client.firstName,
      lastName: client.lastName,
      dateOfBirth: client.dateOfBirth,
      serviceTypeCode: payer.optimalServiceTypeCode,
    };
  }, [payer, client, effectiveProviderId, providerNpi, memberId]);

  const handleResult = useCallback((result: EligibilityCheckResult) => {
    setCheck(result.check);
    setFromCache(result.fromCache);
    setReverifying(false);
  }, []);

  const handleReverify = useCallback(async () => {
    if (!input) return;
    setReverifying(true);
    try {
      const { EligibilityService } = await import('../../services/EligibilityService');
      handleResult(await EligibilityService.verify({ ...input, forceRefresh: true }));
    } catch (error) {
      console.error('[Eligibility] Re-check failed:', error);
      setReverifying(false);
    }
  }, [input, handleResult]);

  return (
    <section
      style={{
        background: COLORS.panel,
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <h3 style={{ margin: '0 0 4px', color: COLORS.text, fontSize: '16px', fontWeight: 600 }}>
        {title}
      </h3>
      <p style={{ margin: '0 0 16px', color: COLORS.label, fontSize: '13px' }}>
        Checks coverage directly with the payer. This is separate from the insurance
        details on file, which are whatever the patient reported.
      </p>

      <div
        style={{
          display: 'grid',
          gap: '14px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          marginBottom: '16px',
        }}
      >
        <PayerPicker
          id={`payer-picker-${client.id}`}
          value={payer}
          onChange={setPayer}
          required
        />

        <div>
          <label
            htmlFor={`member-id-${client.id}`}
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: COLORS.label,
              fontSize: '14px',
            }}
          >
            Member ID
          </label>
          <input
            id={`member-id-${client.id}`}
            type="text"
            value={memberId}
            onChange={(event) => setMemberId(event.target.value)}
            placeholder="As printed on the card"
            style={{
              width: '100%',
              padding: '10px 12px',
              background: COLORS.input,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '0.5rem',
              color: COLORS.text,
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>
      </div>

      <VerifyCoverageButton input={input} onResult={handleResult} />

      {loadingHistory && !check && (
        <p style={{ margin: '14px 0 0', color: COLORS.muted, fontSize: '13px' }}>
          Loading previous checks…
        </p>
      )}

      {check && (
        <div style={{ marginTop: '18px' }}>
          <EligibilityResultCard
            check={check}
            fromCache={fromCache}
            onReverify={input ? handleReverify : undefined}
            reverifying={reverifying}
          />
        </div>
      )}
    </section>
  );
};

export default PatientEligibilityPanel;
